import WithUserContext from './Hoc/WithUserContext'
import IPermissionContextState from 'Models/PermissionContextModel'
import userManager from './Oidc/userManager'
import Const from 'Utils/Consts'
import { useTranslation } from 'react-i18next'
import Main from 'Layouts'
import { registerServiceWorker } from './serviceWorkerRegistration'
import { useEffect, useState } from 'react'
import BottomSheet from 'Components/BottomSheet'
import { Toaster } from 'react-hot-toast'
import Icon from 'Components/Icon'
import UserUpdate from 'Application/Common/Pages/UserUpdate'
import { ToastErrorBottomLeft } from 'Utils/toastService'
import { persistTempVisit } from 'Application/Visits/Actions/TempVisits'
import { clearApiStates } from 'Utils/resetApiStates'
import { useGetUserProfileQuery } from 'Services/api'

type IProps = IPermissionContextState

const App = (props: IProps) => {
  const [showUpdate, setShowUpdate] = useState(false)
  const [showUserUpdate, setShowUserUpdate] = useState(false)
  const [videoEnded, setVideoEnded] = useState(false)

  const [t] = useTranslation()

  const { loading, loaded, isAuth } = props.context

  const { data: userProfileInfo, isError: isUserError } = useGetUserProfileQuery(undefined, {
    skip: !loaded || loading,
  })

  useEffect(() => {
    const video = document.getElementById('video')
    if (!video) return

    const onEnd = () => setVideoEnded(true)
    video.addEventListener('ended', onEnd)

    return () => video.removeEventListener('ended', onEnd)
  }, [])

  useEffect(() => {
    registerServiceWorker(() => setShowUpdate(true))

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('SW updated, reloading...')
      window.location.reload()
    })
  }, [])

  const updateApp = () => {
    navigator.serviceWorker?.controller?.postMessage({ type: 'SKIP_WAITING' })
  }

  useEffect(() => {
    const { loading, loaded, isAuth } = props.context

    if (!loading && isAuth === false) {
      clearApiStates()
      userManager.signinRedirect({ state: { path: window.location.pathname } })
    }

    if (!loading && loaded && isAuth && videoEnded) {
      if (isUserError || !userProfileInfo) {
        setShowUserUpdate(true)
      } else if (userProfileInfo?.errorMessages && userProfileInfo.errorMessages?.length > 0) {
        userProfileInfo.errorMessages.forEach((x: string) => ToastErrorBottomLeft(t(x)))
      }
    }
  }, [props.context, videoEnded, userProfileInfo, isUserError])

  useEffect(() => {
    const worker = new Worker(
      new URL('./Application/Visits/Actions/TempVisitWorker.ts', import.meta.url),
      { type: 'module' },
    )

    worker.postMessage({
      type: 'START',
      data: { interval: Const.env.REACT_APP_VISIT_EXPIRE_WORKER_TIME_IN_SECONDS * 1000 },
    })

    worker.onmessage = e => e.data === 'PersistTempVisits' && persistTempVisit()

    return () => worker.terminate()
  }, [])

  const canLoadApp = !loading && loaded && isAuth && videoEnded

  // ------------------------------
  // MAIN RENDER LOGIC
  // ------------------------------

  if (canLoadApp) {
    return (
      <>
        <BottomSheet
          visible={showUpdate}
          title={t('UPDATE_APP')}
          onCancel={() => setShowUpdate(false)}
        >
          <div className="p-6 text-black">{t('UPDATE_MESSAGE')}</div>
          <div className="cta">
            <button className="btn btn-success btn-lg" onClick={updateApp}>
              {t('UPDATE_APP')}
            </button>
            <button className="btn btn-outline-primary btn-lg" onClick={() => setShowUpdate(false)}>
              {t('CANCEL')}
            </button>
          </div>
        </BottomSheet>

        <UserUpdate showUserUpdate={showUserUpdate} setShowUserUpdate={setShowUserUpdate} />

        <Main />

        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#333',
              color: '#fff',
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '14px',
            },
          }}
        />
      </>
    )
  }

  // Splash screen fallback
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <video
        id="video"
        src="assets/Passargad_Motion_Logo.mp4"
        autoPlay
        muted
        playsInline
        style={{ width: '200px', height: '200px' }}
      />

      {!loading && !loaded && (
        <button
          className="btn btn-primary btn-lg flex gap-2 items-center"
          style={{ position: 'fixed', bottom: '5%' }}
          onClick={() => {
            clearApiStates()
            window.location.reload()
          }}
        >
          <Icon name="rotate-left" className="w-6 h-6" />
          {t('TRY_AGAIN')}
        </button>
      )}
    </div>
  )
}

export default WithUserContext(App)
